import type {
  CutConfig, Layout, MarginsConfig, Pan, SignConfig, Source, ViewMode,
} from "../domain/types";
import { contentSize, computeFitScale, printGapMm } from "../domain/layout";
import { applyDashStyle } from "./dashStyle";

export interface DrawParams {
  hostW: number;
  hostH: number;
  /** Extra space at the top of the host that's covered by the floating navbar. */
  topInset?: number;
  /** Extra space at the bottom (status bar — already excluded from canvas-host height
   *  by the grid layout, but kept here in case the host changes). */
  bottomInset?: number;
  layout: Layout;
  margins: MarginsConfig;
  sign: SignConfig;
  source: Source;
  cut: CutConfig;
  view: ViewMode;
  zoom: "fit" | number;
  pan: Pan;
}

export const computeScale = (params: DrawParams): number => {
  if (params.zoom !== "fit") return params.zoom;
  const { w, h } = contentSize(params.layout, params.view, params.sign);
  const visibleH = Math.max(1, params.hostH - (params.topInset ?? 0) - (params.bottomInset ?? 0));
  return computeFitScale(params.hostW, visibleH, w, h);
};

const computeOrigin = (
  hostW: number,
  hostH: number,
  contentW: number,
  contentH: number,
  scale: number,
  pan: Pan,
  topInset = 0,
  bottomInset = 0,
): { x: number; y: number } => ({
  x: (hostW - contentW * scale) / 2 + pan.x,
  y: topInset + (hostH - topInset - bottomInset - contentH * scale) / 2 + pan.y,
});

export const drawCanvas = (
  canvas: HTMLCanvasElement,
  params: DrawParams | null,
  hostW: number,
  hostH: number,
): void => {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = hostW * dpr;
  canvas.height = hostH * dpr;
  canvas.style.width  = `${hostW}px`;
  canvas.style.height = `${hostH}px`;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!params) return;
  if (params.view === "print") drawPrintView(ctx, params);
  else drawAssembledView(ctx, params);
};

const drawPrintView = (ctx: CanvasRenderingContext2D, p: DrawParams): void => {
  const { layout: L, margins, source, cut, sign } = p;
  const gapMm = printGapMm(L.paper);
  const totalW = L.cols * L.paper.width  + (L.cols - 1) * gapMm;
  const totalH = L.rows * L.paper.height + (L.rows - 1) * gapMm;
  const scale = computeScale(p);
  const { x: offX, y: offY } = computeOrigin(
    p.hostW, p.hostH, totalW, totalH, scale, p.pan, p.topInset ?? 0, p.bottomInset ?? 0,
  );

  for (let r = 0; r < L.rows; r++) {
    for (let c = 0; c < L.cols; c++) {
      const px = offX + c * (L.paper.width  + gapMm) * scale;
      const py = offY + r * (L.paper.height + gapMm) * scale;
      const pw = L.paper.width  * scale;
      const ph = L.paper.height * scale;

      // paper with soft drop shadow (Tahoe / Liquid Glass — paper floats on light canvas)
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.16)";
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 4;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(px, py, pw, ph);
      ctx.restore();

      ctx.strokeStyle = "rgba(0,0,0,0.18)";
      ctx.lineWidth = 1;
      ctx.strokeRect(px + 0.5, py + 0.5, pw - 1, ph - 1);

      const ax = px + margins.left * scale;
      const ay = py + margins.top  * scale;
      const aw = L.availW * scale;
      const ah = L.availH * scale;

      // margin shading
      ctx.fillStyle = "rgba(38, 128, 235, 0.08)";
      ctx.fillRect(px, py, pw, margins.top * scale);
      ctx.fillRect(px, py + ph - margins.bottom * scale, pw, margins.bottom * scale);
      ctx.fillRect(px, ay, margins.left  * scale, ah);
      ctx.fillRect(px + pw - margins.right * scale, ay, margins.right * scale, ah);

      drawSignSlice(ctx, c, r, L, ax, ay, aw, ah, scale, sign, source);

      // cut guide centred on the printable boundary (the cut line)
      if (cut.enabled) {
        const tk = Math.max(0.5, cut.thicknessMm * scale);
        ctx.save();
        ctx.strokeStyle = "#000";
        ctx.lineWidth = tk;
        applyDashStyle(ctx, cut.style, tk);
        ctx.strokeRect(ax, ay, aw, ah);
        ctx.restore();
      }

      // soft dashed printable-area outline (preview only, not exported)
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "rgba(38, 128, 235, 0.55)";
      ctx.lineWidth = 1;
      ctx.strokeRect(ax + 0.5, ay + 0.5, aw - 1, ah - 1);
      ctx.restore();

      // page number badge
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.font = "10px ui-monospace, monospace";
      ctx.textBaseline = "top";
      ctx.fillText(`${r * L.cols + c + 1}/${L.cols * L.rows}`, px + 4, py + 4);
    }
  }
};

const drawSignSlice = (
  ctx: CanvasRenderingContext2D,
  col: number,
  row: number,
  L: Layout,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  scale: number,
  sign: SignConfig,
  source: Source,
): void => {
  const sxMm = col * L.availW;
  const syMm = row * L.availH;
  const swMm = Math.min(L.availW, sign.width  - sxMm);
  const shMm = Math.min(L.availH, sign.height - syMm);
  if (swMm <= 0 || shMm <= 0) return;

  const pxPerMmX = source.widthPx  / sign.width;
  const pxPerMmY = source.heightPx / sign.height;

  ctx.save();
  ctx.beginPath();
  ctx.rect(dx, dy, dw, dh);
  ctx.clip();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    source.image,
    sxMm * pxPerMmX, syMm * pxPerMmY,
    swMm * pxPerMmX, shMm * pxPerMmY,
    dx, dy,
    swMm * scale, shMm * scale,
  );
  ctx.restore();
};

const drawAssembledView = (ctx: CanvasRenderingContext2D, p: DrawParams): void => {
  const { layout: L, sign, source } = p;
  const scale = computeScale(p);
  const { x: offX, y: offY } = computeOrigin(
    p.hostW, p.hostH, sign.width, sign.height, scale, p.pan, p.topInset ?? 0, p.bottomInset ?? 0,
  );

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.18)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 5;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(offX, offY, sign.width * scale, sign.height * scale);
  ctx.restore();

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source.image, offX, offY, sign.width * scale, sign.height * scale);

  ctx.save();
  ctx.strokeStyle = "rgba(231, 76, 60, 0.85)";
  ctx.lineWidth = 1;
  ctx.setLineDash([6, 4]);
  for (let i = 1; i < L.cols; i++) {
    const x = offX + i * L.availW * scale;
    ctx.beginPath();
    ctx.moveTo(x, offY);
    ctx.lineTo(x, offY + sign.height * scale);
    ctx.stroke();
  }
  for (let i = 1; i < L.rows; i++) {
    const y = offY + i * L.availH * scale;
    ctx.beginPath();
    ctx.moveTo(offX, y);
    ctx.lineTo(offX + sign.width * scale, y);
    ctx.stroke();
  }
  ctx.restore();

  ctx.strokeStyle = "rgba(0,0,0,0.18)";
  ctx.lineWidth = 1;
  ctx.strokeRect(offX + 0.5, offY + 0.5, sign.width * scale - 1, sign.height * scale - 1);
};
